/**
 * <sm-copy-link url="…" done-label="Copied"> — copies a URL to the clipboard.
 *
 * Wraps a real <button> that is rendered by Liquid, so the label stays editable
 * and the control is there before this script runs; without JavaScript the
 * button is simply removed rather than sitting dead on the page.
 */
class SmCopyLink extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.label = this.button.querySelector('[data-copy-label]') || this.button;
    this.idle = this.label.textContent;
    this.done = this.getAttribute('done-label') || 'Copied';
    this.url = this.getAttribute('url') || window.location.href;

    // Only reveal it once we can actually do the job.
    this.hidden = false;
    this.button.addEventListener('click', () => this.copy());
  }

  async copy() {
    let ok = false;
    try {
      // Needs a secure context; a theme preview over plain http does not have one.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.url);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }

    if (!ok) ok = this.copyBySelection();

    this.flash(ok);
  }

  /** Fallback for insecure contexts and older Safari. */
  copyBySelection() {
    const field = document.createElement('textarea');
    field.value = this.url;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, field.value.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    field.remove();
    return ok;
  }

  flash(ok) {
    // Say what actually happened rather than claiming success either way. No
    // prompt() fallback — an unexpected dialog is worse than a quiet failure.
    this.setAttribute('data-state', ok ? 'copied' : 'failed');
    this.label.textContent = ok ? this.done : this.getAttribute('failed-label') || this.idle;
    this.reset();
  }

  reset() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.label.textContent = this.idle;
      this.removeAttribute('data-state');
    }, 2200);
  }
}

if (!customElements.get('sm-copy-link')) {
  customElements.define('sm-copy-link', SmCopyLink);
}
